/**
 * ModernFusion — Urban Indian engagement.
 *
 * Minimalist, glassmorphism design with sage/dusty rose/champagne palette.
 * Story timeline, events with dress codes, and dietary preferences.
 */

import React from 'react';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import { resolveContent } from './content';
import { modernTheme, themeCssVars } from './theme';
import { HeroSection } from './sections/HeroSection';
import { RsvpSection } from './sections/RsvpSection';

const META: TemplateMetadata = {
  id: 'modern-fusion-experience',
  name: 'Modern Fusion — Urban Indian',
  occasionType: 'ENGAGEMENT',
  description:
    'A contemporary Indian engagement: glassmorphism hero, story timeline, events with dress codes, and RSVP with dietary preferences.',
  isPremium: true,
  tags: ['modern', 'urban', 'fusion', 'minimalist', 'glassmorphism', 'engagement'],
  colorScheme: {
    primary: modernTheme.sage,
    accent: modernTheme.rose,
    bg: modernTheme.cream,
  },
};

export function ModernFusionTemplate({ data }: { data: InvitationData }) {
  const content = React.useMemo(() => resolveContent(data), [data]);
  const invitationId = data.invitationId ?? data.id ?? null;
  const isLive = data.status === 'PUBLISHED' || data.status === 'PROCESSING';

  const scrollToRsvp = React.useCallback(() => {
    document.getElementById('rsvp')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return (
    <main
      className="modern-root min-h-screen scroll-smooth bg-[var(--modern-cream)] font-sans text-[var(--modern-ink)] antialiased"
      style={themeCssVars(modernTheme)}
    >
      <HeroSection content={content.hero} onRsvp={scrollToRsvp} />

      {/* Story section inline */}
      <section className="px-6 py-16 text-center">
        <p className="text-sm uppercase tracking-[0.3em] text-[var(--modern-sage)]">
          {content.story.label}
        </p>
        <h2 className="mt-2 font-serif text-3xl font-semibold text-[var(--modern-sage-dark)]">
          {content.story.title}
        </h2>
        <div className="mx-auto mt-8 grid max-w-3xl gap-6 sm:grid-cols-3">
          {content.story.chapters.map((chapter, i) => (
            <div
              key={chapter.title}
              className="rounded-2xl border border-[var(--modern-border)] bg-white p-6"
            >
              <h3 className="font-medium text-[var(--modern-sage-dark)]">{chapter.title}</h3>
              <p className="mt-2 text-sm text-[var(--modern-muted)]">{chapter.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Events section inline */}
      <section className="px-6 py-16" style={{ background: 'var(--modern-rose-soft)' }}>
        <p className="text-center text-sm uppercase tracking-[0.3em] text-[var(--modern-sage)]">
          {content.events.label}
        </p>
        <h2 className="mt-2 text-center font-serif text-3xl font-semibold text-[var(--modern-sage-dark)]">
          {content.events.title}
        </h2>
        <div className="mx-auto mt-8 grid max-w-4xl gap-6">
          {content.events.items.map((event, i) => (
            <div
              key={event.title}
              className="rounded-2xl border border-[var(--modern-border)] bg-white p-6"
            >
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="font-serif text-xl text-[var(--modern-sage-dark)]">{event.title}</h3>
                {event.dressCode && (
                  <span className="rounded-full bg-[var(--modern-champagne-soft)] px-3 py-1 text-xs text-[var(--modern-ink)]">
                    Dress: {event.dressCode}
                  </span>
                )}
              </div>
              <p className="mt-2 text-sm text-[var(--modern-muted)]">{event.description}</p>
              <div className="mt-3 flex flex-wrap gap-4 text-sm text-[var(--modern-sage)]">
                <span>{event.date}</span>
                <span>{event.time}</span>
                <span>{event.venue}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      <RsvpSection content={content.rsvp} invitationId={invitationId} isLive={isLive} />
    </main>
  );
}

registry.register({ metadata: META, component: ModernFusionTemplate });
